import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import useSeo from "@/hooks/useSeo";
import { faqGroups } from "@/mocks/faq";

export default function FaqPage() {
  const origin = window.location.origin;
  const allItems = faqGroups.flatMap((group) => group.items);

  useSeo({
    title: "常见问题 - 搜索技巧与内容说明 - 15黑料网",
    description:
      "15黑料网常见问题汇总：如何用关键词快速定位汇总、标签与分类有什么区别、词条多久更新一次、是否会收录个人隐私信息，以及内容纠错与线索提交方式。",
    keywords: "15黑料网常见问题,黑料搜索技巧,黑料标签,分类查找,词条更新",
    path: "/faq",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: allItems.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: origin },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${origin}/faq` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "常见问题" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">常见问题</h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里整理了用户最常问的两类问题：怎么用检索更高效，以及词条内容是怎么来的。如果还有疑问，欢迎通过页面底部的联系方式告诉我们。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid gap-10 lg:grid-cols-[240px_1fr] lg:gap-14">
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <h2 className="text-sm font-semibold text-foreground-950">问题分类</h2>
              <nav aria-label="问题分类导航" className="mt-4 space-y-1">
                {faqGroups.map((group, index) => (
                  <a
                    key={group.group}
                    href={`#faq-group-${index}`}
                    className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                  >
                    {group.group}
                    <span className="text-xs text-foreground-400">{group.items.length}</span>
                  </a>
                ))}
              </nav>
              <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100/60 p-4">
                <p className="text-xs leading-relaxed text-foreground-600">
                  没找到答案？
                  <Link to="/about" className="ml-1 text-primary-600 transition-colors hover:text-primary-700">
                    联系我们
                  </Link>
                </p>
              </div>
            </aside>

            <div className="space-y-12">
              {faqGroups.map((group, index) => (
                <section key={group.group} id={`faq-group-${index}`} className="scroll-mt-28" aria-labelledby={`faq-title-${index}`}>
                  <h2 id={`faq-title-${index}`} className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {group.group}
                  </h2>
                  <div className="mt-5">
                    <FaqAccordion items={group.items} defaultOpen={index === 0 ? 0 : -1} />
                  </div>
                </section>
              ))}

              <div className="rounded-lg bg-foreground-950 p-6 md:p-8">
                <h2 className="font-heading text-lg font-bold text-background-50 md:text-xl">还想了解更多？</h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
                  关于本站的内容原则、整理流程与联系方式，都写在「关于我们」页面里。
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    关于我们
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <Link
                    to="/keywords"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-700 px-5 py-2.5 text-sm text-background-200 transition-colors hover:border-accent-400 hover:text-accent-300"
                  >
                    去查关键词
                    <i className="ri-search-line" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}